export function capitalize(str: string): string {
    if (!str) {
        return str;
    }
    return str.charAt(0).toLocaleUpperCase() + str.slice(1);
}

export function uncapitalize(str: string): string {
    if (!str) {
        return str;
    }
    return str.charAt(0).toLocaleLowerCase() + str.slice(1);
}

/**
 * Trims (removes whitespace) from the ends of a string, and if needed truncates to a given length. Optionally adds suffix if truncated.
 * @param str The string to truncate.
 * @param len Length to truncate to.
 * @param suffix Optional. Stick this at the end of the string if it did need to be truncated.
 */
export function truncateTrim(str: string, len: number, suffix?: string): string {
    suffix = suffix || '';
    str = str ? str.trim() : str;
    return str && str.length > len ? str.slice(0, len).trim() + suffix : str;
}

export function parseSearchTokens(term: string): Array<string> {
    return term.trim().toLocaleLowerCase().replace(/\s+/g, ' ').split(' ');
}

/** Parse given search string into tokens and returns true if they all exist in the given subject */
export function stringSearch(subject: string, search: string): boolean {
    const tokens = parseSearchTokens(search);
    const searchIn = subject.toLocaleLowerCase();
    return tokens.every(token => searchIn.indexOf(token) > -1);
}

export function pluralize(singularForm: string, howMany: number, pluralForm?: string): string {
    return howMany == 1 ? singularForm : pluralForm || `${singularForm}s`;
}

export function removeStartingDigits(str: string): string {
    if (!str) {
        return str;
    }

    return str.replace(/^[0-9]*/, '');
}

export function spacedOrUnderscoredWordsToPascalCase(str: string): string {
    if (!str) {
        return str;
    }

    return str
        .split(/[\s_]/)
        .map(word => word.charAt(0).toLocaleUpperCase() + word.slice(1))
        .join('');
}

export function spacedOrUnderscoredWordsToSnakeCase(str: string): string {
    if (!str) {
        return str;
    }

    return str
        .split(/[\s_]/)
        .filter(word => word)
        .map(word => word.toLocaleLowerCase())
        .join('_');
}

export function reduceToAlphanumeric(str: string): string {
    return str.replace(/[\W_]+/g, '');
}

export function getCapitals(str: string): RegExpMatchArray | null {
    return str.match(/[A-Z]{1}/g);
}

export function convertTagStringToList(tags?: string | null, truncateTo = 0): string[] {
    const allTags = tags?.split(',') || [];
    allTags.forEach((t, i) => (allTags[i] = t.trim()));
    const someTags = truncateTo ? allTags.slice(0, truncateTo) : allTags;
    const hiddenTags = allTags.length - someTags.length;
    if (hiddenTags > 0) {
        someTags.push(`+${allTags.length - someTags.length}`);
    }

    return someTags;
}

export function isFalsyOrEmpty(str?: string | null): boolean {
    return !str || str.trim().length === 0;
}

export function isNonEmptyOrWhitespaceString(unknownData: unknown): boolean {
    return typeof unknownData === 'string' && (<string>unknownData.trim()).length > 0;
}

const htmlCharMap: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
};

export function escapeHtml(str: string): string {
    if (!str) {
        return str;
    }
    return String(str).replace(/[&<>"'`=/]/g, s => htmlCharMap[s]);
}

export function areEqual(str1: string | null | undefined, str2: string | null | undefined) {
    return str1 == str2 || (!str1 && !str2);
}

export function cronExpressionsAreEqual(str1: string | null | undefined, str2: string | null | undefined) {
    return str1?.replace('*/1', '*') == str2?.replace('*/1', '*') || (!str1 && !str2);
}
